// src/pages/MainDashboardPage.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { hasRole, hasFullAdminAccess } from '../services/authApi';
import AlertsSection from '../components/Dashboard/AlertsSection';
import PerformanceMetricWidget from '../components/Dashboard/PerformanceMetricWidget';
import KpiStatsCards from '../components/Dashboard/KpiStatsCards';
import TaskSummaryCards from '../components/Dashboard/TaskSummaryCards';
import TasksViewTable from '../components/Dashboard/TasksViewTable';
import ProjectsQuickAccess from '../components/Dashboard/ProjectsQuickAccess';
import ProjectStatusSummary from '../components/Dashboard/ProjectStatusSummary';
import { getAirports, Airport } from '../services/airportApi';
import { listProjects } from '../services/projectApi';

export default function MainDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  // @ts-ignore - intentionally unused
  const _navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userAirportId, setUserAirportId] = useState<number | undefined>(undefined);
  const [userAirports, setUserAirports] = useState<Airport[]>([]); // All airports where user is manager
  const [selectedAirportId, setSelectedAirportId] = useState<number | undefined>(undefined);
  const [taskFilters, setTaskFilters] = useState<{
    pastDueOnly?: boolean;
    upcomingDays?: number;
    status?: string;
    myInProgressActionsOnly?: boolean;
  }>({});

  // Determine user role and permissions (explicit for role-based home layout)
  const isAdmin = user ? hasFullAdminAccess(user) : false; // System Admin only
  const isExecAdmin = user ? hasRole(user, 'Exec Admin') : false;
  const isManager = user ? hasRole(user, 'Airport Manager') : false;
  const isFraportUser = user ? hasRole(user, 'Fraport User') : false;
  const isTenantUser = user ? hasRole(user, 'Tenant User (365 Tenant Sense)') : false;
  const isClientUser = user ? hasRole(user, 'Client User') : false;
  const isRegularUser = isFraportUser || isTenantUser || isClientUser; // Task widget + user past-due alerts

  // Load user's airport associations (for managers) - Phase 2 Sprint 27: Support multiple airports
  useEffect(() => {
    const loadUserAirports = async () => {
      if (isManager && user?.userId) {
        try {
          console.log('MainDashboardPage: Loading airports for Airport Manager, userId:', user.userId);
          // Fetch airports and find ALL airports where user is a manager
          const airportsResponse = await getAirports();
          console.log('MainDashboardPage: Airports response:', airportsResponse);
          console.log('MainDashboardPage: Number of airports:', airportsResponse.airports?.length);
          
          // Find ALL airports where user is a manager (not just the first one)
          const assignedAirports = airportsResponse.airports.filter(
            (airport) => airport.managers?.some((m) => m.userId === user.userId)
          );

          // Show manager airports immediately so the page doesn't block on projects
          let airportsToShow: Airport[] = [...assignedAirports];
          if (airportsToShow.length > 0) {
            setUserAirports(airportsToShow);
            const firstAirportId = airportsToShow[0].airportId;
            setUserAirportId(firstAirportId);
            setSelectedAirportId(firstAirportId);
          } else {
            setUserAirports([]);
            setUserAirportId(undefined);
            setSelectedAirportId(undefined);
            setError('Unable to determine which airport you manage. Please contact an administrator.');
          }

          // Load airports from assigned projects in the background (do not block initial render)
          listProjects({ page: 1, pageSize: 100 })
            .then((projectsResponse) => {
              const projectAirportIds = [...new Set(
                (projectsResponse.projects || []).map(p => p.airportId).filter((id): id is number => id != null && id !== undefined)
              )];
              const assignedIds = new Set(assignedAirports.map(a => a.airportId));
              const extraIds = projectAirportIds.filter(id => !assignedIds.has(id));
              if (extraIds.length > 0) {
                const extraAirports = airportsResponse.airports.filter(a => extraIds.includes(a.airportId));
                const merged = [...assignedAirports, ...extraAirports];
                setUserAirports(merged);
                if (assignedAirports.length === 0 && merged.length > 0) {
                  setError('');
                  const firstId = merged[0].airportId;
                  setUserAirportId(firstId);
                  setSelectedAirportId(firstId);
                }
              }
            })
            .catch(() => {
              // Keep manager airports only if projects fetch fails
            });
        } catch (err) {
          console.error('MainDashboardPage: Error loading user airports:', err);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const error = err as any;
          if (error.error && error.message) {
            console.error('MainDashboardPage: Airport API error:', error.message, error.code);
            setError(`Failed to load airport information: ${error.message}`);
          } else {
            setError('Failed to load airport information. Please refresh the page.');
          }
          setUserAirports([]);
          setUserAirportId(undefined);
          setSelectedAirportId(undefined);
        }
      } else if (isManager && !user?.userId) {
        console.warn('MainDashboardPage: User is Airport Manager but userId is missing');
        setError('User information incomplete. Please refresh the page.');
        setUserAirports([]);
        setUserAirportId(undefined);
        setSelectedAirportId(undefined);
      } else {
        setUserAirports([]);
        setUserAirportId(undefined);
        setSelectedAirportId(undefined);
      }
      setLoading(false);
    };

    if (!authLoading && user) {
      loadUserAirports();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading, isManager]);

  if (authLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">You must be logged in to view the dashboard.</Alert>
      </Container>
    );
  }

  // Use selectedAirportId if available, otherwise fall back to userAirportId
  const activeAirportId = selectedAirportId ?? userAirportId;

  // Role-based alert source for AlertsSection (sprint home page redesign)
  const alertSource = isAdmin
    ? 'system'
    : isExecAdmin
      ? 'systemPastDue'
      : isManager
        ? 'airportPastDue'
        : isRegularUser
          ? 'userPastDue'
          : undefined;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ color: 'white' }}>
          Project Portfolio
        </Typography>

        {/* Airport Selector for Managers with Multiple Airports */}
        {isManager && userAirports.length > 1 && (
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: 'white' }}>Select Airport</InputLabel>
            <Select
              value={selectedAirportId || ''}
              onChange={(e) => setSelectedAirportId(e.target.value as number)}
              label="Select Airport"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'white',
                },
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              }}
            >
              {userAirports.map((airport) => (
                <MenuItem key={airport.airportId} value={airport.airportId}>
                  {airport.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Alerts Section - Role-based source */}
      {(isAdmin || isExecAdmin || isManager || isRegularUser) && (
        <Box sx={{ mb: 3 }}>
          <AlertsSection
            userId={user.userId}
            airportId={isManager ? activeAirportId : undefined}
            isAdmin={alertSource === 'system' ? true : undefined}
            alertSource={alertSource}
          />
        </Box>
      )}

      {/* System Admin Home: Alerts + Recent Projects only */}
      {isAdmin && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <ProjectsQuickAccess type="system" />
            </Grid>
          </Grid>
        </>
      )}

      {/* Exec Admin Home: Alerts (past due) + Project Status Summary (all) + Recent Projects */}
      {!isAdmin && isExecAdmin && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <ProjectStatusSummary airportId={undefined} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProjectsQuickAccess type="system" />
            </Grid>
          </Grid>
        </>
      )}

      {/* Airport Manager Home: Alerts (past due) + Project Status Summary + Recent Projects */}
      {!isAdmin && !isExecAdmin && isManager && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <ProjectStatusSummary airportId={activeAirportId} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ProjectsQuickAccess
                type="airport"
                airportId={activeAirportId}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Fraport / Tenant / Client User Home: Alerts (past due) + Task Widget; Tenant also gets project list (In Progress default) */}
      {!isAdmin && !isExecAdmin && !isManager && isRegularUser && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TaskSummaryCards
                type="user"
                userId={user.userId}
                onFilterChange={setTaskFilters}
                defaultActiveFilter={(isTenantUser || isClientUser) ? 'inProgress' : undefined}
                myInProgressActionsOnly={isTenantUser || isClientUser}
                hiddenCardTypes={(isTenantUser || isClientUser) ? ['total', 'upcoming'] : undefined}
              />
            </Grid>
          </Grid>
          <Box sx={{ mb: 3 }}>
            <TasksViewTable
              type="user"
              userId={user.userId}
              defaultExpanded={true}
              initialFilters={taskFilters}
              defaultFiltersForTenantOrClientUser={
                isTenantUser || isClientUser
                  ? { status: 'IN_PROGRESS', myInProgressActionsOnly: true }
                  : undefined
              }
            />
          </Box>
          {/* Tenant / Client: project list with All | In Progress tabs (default In Progress) */}
          {(isTenantUser || isClientUser) && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <ProjectsQuickAccess
                  type="user"
                  userId={user.userId}
                  hidePortfolioLink
                />
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Legacy layout: roles that don't match the new role-based layout (e.g. no role or unknown) */}
      {!isAdmin && !isExecAdmin && !isManager && !isRegularUser && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <PerformanceMetricWidget
                type="user"
                userId={user.userId}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <KpiStatsCards type="user" userId={user.userId} />
            </Grid>
          </Grid>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TaskSummaryCards
                type="user"
                userId={user.userId}
                onFilterChange={setTaskFilters}
              />
            </Grid>
          </Grid>
          <Box sx={{ mb: 3 }}>
            <TasksViewTable
              type="user"
              userId={user.userId}
              defaultExpanded={true}
              initialFilters={taskFilters}
              defaultFiltersForTenantOrClientUser={
                isTenantUser || isClientUser
                  ? { status: 'IN_PROGRESS', myInProgressActionsOnly: true }
                  : undefined
              }
            />
          </Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <ProjectsQuickAccess type="user" userId={user.userId} />
            </Grid>
          </Grid>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Container>
  );
}

